import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useSelector } from "react-redux"
import { useNavigate, useOutletContext } from "react-router-dom"
import CityHubTabHeader from "../CityHubTabHeader"
import {
  CityHubInfiniteSentinel,
  PhotoCard,
  PhotoCardSkeleton,
} from "../CityHubCards"
import { AddPhotoModal, BecomeContributorModal, PhotographyPreviewModal } from "../CityHubModals"
import { CITY_HUB_INFINITE_PAGE_SIZE, PUBLIC_CITY_INFINITE_PAGE_SIZE, unwrapCityHubPagedResult } from "../cityHubTabs"
import EmptyState from "../../../components/Common/EmptyState"
import DeleteConfirmation from "../../../components/Common/DeleteConfirmation"
import useInfinitePagination from "../../../hooks/useInfinitePagination"
import useCategories from "../../../hooks/useCategories"
import { del, get, post } from "../../../api/axios"
import endpoints from "../../../api/endpoints"
import { toastApiError } from "../../../helpers/apiError"
import { Bounce, toast } from "react-toastify"
import {
  PHOTOGRAPHY_CTA,
  fetchCityPhotographyRequest,
  getPhotographyCtaLabel,
  isAcceptedPhotoRequest,
} from "../cityPhotographyRequest"
import {
  PHOTOGRAPHY_LIST_WITH,
  canManagePhotography,
  fetchPublicPhotographyDetail,
  interpretPhotographyReactResult,
  mapApiPhotography,
  patchPhotographyLike,
} from "../cityPhotography"
import { goToRequestInvite } from "../../PublicCity/publicCityPaths"

const TOAST_OPTIONS = {
  position: "bottom-center",
  theme: "colored",
  transition: Bounce,
}

const SKELETON_COUNT = 4
const LOAD_MORE_SKELETON_COUNT = 2

const LIKES_FILTER_OPTIONS = [
  { value: "1", label: "Liked" },
  { value: "0", label: "Not liked" },
]

const normalizeSingleFilter = next =>
  Array.isArray(next) ? next[0] || "" : next || ""

const mergePhotoPages = (prev, next) => {
  const seen = new Set(prev.map(item => String(item.id)))
  return [
    ...prev,
    ...next.filter(item => item?.id && !seen.has(String(item.id))),
  ]
}

const CityHubPhotographyTab = () => {
  const navigate = useNavigate()
  const {
    tab,
    city,
    isCityLoading = false,
    isCityMember = false,
    isPublic = false,
    listScrollRootRef,
    refreshRecentPhotos,
  } = useOutletContext()
  const cityId = city?.id || null
  const currentUser = useSelector(state => state.auth.currentUser)
  const currentUserId = currentUser?.id || null
  const { categories } = useCategories()

  const [search, setSearch] = useState("")
  const [debouncedSearch, setDebouncedSearch] = useState("")
  const [getLikesFilter, setGetLikesFilter] = useState("")
  const [categoryId, setCategoryId] = useState("")
  const [photos, setPhotos] = useState([])
  const [hasMore, setHasMore] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const [error, setError] = useState(null)
  const sentinelRef = useRef(null)
  const pageRef = useRef(1)
  const loadInFlightRef = useRef(false)

  const [contributionRequest, setContributionRequest] = useState(null)
  const [isRequestLoading, setIsRequestLoading] = useState(true)

  const [isBecomeOpen, setIsBecomeOpen] = useState(false)
  const [isPhotoOpen, setIsPhotoOpen] = useState(false)
  const [editingPhotoId, setEditingPhotoId] = useState(null)
  const [previewPhotoId, setPreviewPhotoId] = useState(null)
  const [deletingPhoto, setDeletingPhoto] = useState(null)
  const [isDeleting, setIsDeleting] = useState(false)
  const [likingId, setLikingId] = useState(null)

  const ctaLabel = getPhotographyCtaLabel(contributionRequest)
  const canUpload = isAcceptedPhotoRequest(contributionRequest)
  const ctaBusy = isCityLoading || isRequestLoading
  const canContribute = Boolean(cityId && isCityMember && !isPublic)

  const openInvite = useCallback(() => {
    goToRequestInvite(
      navigate,
      { from: "public-city", cityId, tab: "photography", city },
      { isAuthenticated: Boolean(currentUserId) },
    )
  }, [city, cityId, currentUserId, navigate])

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), 300)
    return () => clearTimeout(timer)
  }, [search])

  const loadContributionRequest = useCallback(
    async ({ signal } = {}) => {
      if (!cityId || isPublic) {
        setContributionRequest(null)
        setIsRequestLoading(false)
        return
      }

      setIsRequestLoading(true)
      const result = await fetchCityPhotographyRequest({
        cityId,
        userId: currentUserId,
        signal,
      })

      if (signal?.aborted || result?.aborted) return

      if (result?.ok) {
        setContributionRequest(result.request)
      }
      setIsRequestLoading(false)
    },
    [cityId, currentUserId, isPublic],
  )

  useEffect(() => {
    const controller = new AbortController()
    setContributionRequest(null)
    loadContributionRequest({ signal: controller.signal })
    return () => controller.abort()
  }, [loadContributionRequest])

  const fetchPhotos = useCallback(
    async ({ page: pageToLoad = 1, append = false } = {}) => {
      if (!cityId) {
        setPhotos([])
        setHasMore(false)
        setError(null)
        setIsLoading(false)
        setIsLoadingMore(false)
        return
      }

      if (append) setIsLoadingMore(true)
      else {
        setIsLoading(true)
        setError(null)
      }

      try {
        const pageSize = isPublic
          ? PUBLIC_CITY_INFINITE_PAGE_SIZE
          : CITY_HUB_INFINITE_PAGE_SIZE
        const params = {
          page: pageToLoad,
          per_page: pageSize,
          page_size: pageSize,
          search: debouncedSearch || "",
          "with[]": PHOTOGRAPHY_LIST_WITH,
          "city_ids[]": cityId,
        }
        if (getLikesFilter !== "" && !isPublic) {
          params.is_liked = Number(getLikesFilter)
        }
        if (categoryId) params["tag_ids[]"] = categoryId

        const response = await get(
          isPublic ? endpoints.getPublicPhotography : endpoints.getPhotography,
          { params },
        )

        if (!response?.data?.success) {
          const message = toastApiError(response, "Failed to load photography")
          setError(message)
          if (!append) setPhotos([])
          setHasMore(false)
          return
        }

        const { list, currentPage, hasMore: nextHasMore } =
          unwrapCityHubPagedResult(response, {
            pageToLoad,
            pageSize,
          })
        const mapped = list.map(mapApiPhotography)

        setPhotos(prev => (append ? mergePhotoPages(prev, mapped) : mapped))
        pageRef.current = currentPage
        setHasMore(nextHasMore)
      } catch (err) {
        const message = toastApiError(err, "Failed to load photography")
        setError(message)
        if (!append) setPhotos([])
        setHasMore(false)
      } finally {
        loadInFlightRef.current = false
        setIsLoading(false)
        setIsLoadingMore(false)
      }
    },
    [cityId, debouncedSearch, getLikesFilter, categoryId, isPublic],
  )

  useEffect(() => {
    pageRef.current = 1
    fetchPhotos({ page: 1, append: false })
  }, [fetchPhotos])

  const loadMore = useCallback(() => {
    if (
      !hasMore ||
      isLoading ||
      isLoadingMore ||
      loadInFlightRef.current
    ) {
      return
    }
    loadInFlightRef.current = true
    fetchPhotos({ page: pageRef.current + 1, append: true })
  }, [fetchPhotos, hasMore, isLoading, isLoadingMore])

  useInfinitePagination({
    hasMore: hasMore && !isLoading,
    isLoading: isLoadingMore,
    onLoadMore: loadMore,
    resetKey: photos.length,
    sentinelRef,
    rootRef: listScrollRootRef,
  })

  const filterConfig = useMemo(
    () => [
      {
        key: "is_liked",
        type: "select",
        label: "Likes",
        multiple: false,
        options: LIKES_FILTER_OPTIONS,
        value: getLikesFilter,
        onChange: next => {
          setGetLikesFilter(normalizeSingleFilter(next))
        },
      },
      {
        key: "tag_ids",
        type: "select",
        label: "Category",
        multiple: false,
        searchable: true,
        options: categories,
        value: categoryId,
        onChange: next => {
          setCategoryId(normalizeSingleFilter(next))
        },
      },
    ],
    [getLikesFilter, categoryId, categories],
  )

  const resetPhotoForm = () => {
    setEditingPhotoId(null)
  }

  const handleCtaClick = () => {
    if (!cityId || ctaBusy || !canContribute) return
    if (canUpload) {
      setEditingPhotoId(null)
      setIsPhotoOpen(true)
      return
    }
    setIsBecomeOpen(true)
  }

  const fetchPublicPhotoPreview = useCallback(
    async (id, options = {}) => {
      const fromList = photos.find(item => String(item.id) === String(id))
      if (fromList?.raw?.id) {
        return { ok: true, item: fromList.raw }
      }
      return fetchPublicPhotographyDetail(id, options)
    },
    [photos],
  )

  const handleToggleLike = useCallback(
    async photo => {
      if (!photo?.id || likingId) return

      const previousLiked = Boolean(photo.liked)
      const previousLikes = Number(photo.likes || 0)
      const nextLiked = !previousLiked
      const nextLikes = Math.max(0, previousLikes + (previousLiked ? -1 : 1))

      setLikingId(photo.id)
      setPhotos(prev =>
        patchPhotographyLike(prev, photo.id, {
          liked: nextLiked,
          likes: nextLikes,
        }),
      )

      try {
        const response = await post(
          endpoints.togglePhotographyReaction(photo.id),
          {},
        )

        if (!response?.data?.success) {
          setPhotos(prev =>
            patchPhotographyLike(prev, photo.id, {
              liked: previousLiked,
              likes: previousLikes,
            }),
          )
          toastApiError(response, "Failed to update reaction")
          return
        }

        const interpreted = interpretPhotographyReactResult(
          response,
          previousLiked,
        )
        setPhotos(prev =>
          patchPhotographyLike(prev, photo.id, {
            liked: interpreted.liked,
            likes: interpreted.likes ?? nextLikes,
          }),
        )
      } catch (err) {
        setPhotos(prev =>
          patchPhotographyLike(prev, photo.id, {
            liked: previousLiked,
            likes: previousLikes,
          }),
        )
        toastApiError(err, "Failed to update reaction")
      } finally {
        setLikingId(null)
      }
    },
    [likingId],
  )

  const handleEditPhoto = photo => {
    if (!photo?.id) return
    setEditingPhotoId(photo.id)
    setIsPhotoOpen(true)
  }

  const handleDeleteConfirm = async () => {
    if (!deletingPhoto?.id) return
    const deleteCityId = deletingPhoto.cityId || cityId
    if (!deleteCityId) {
      toast.error("Select a city in the banner first.", TOAST_OPTIONS)
      return
    }

    setIsDeleting(true)
    try {
      const response = await del(endpoints.deletePhotography(deletingPhoto.id), {
        params: { city_id: deleteCityId },
      })
      if (!response?.data?.success) {
        toastApiError(response, "Failed to delete photography")
        return
      }
      toast.success(response.data.message || "Photography deleted", TOAST_OPTIONS)
      setDeletingPhoto(null)
      setPhotos(prev =>
        prev.filter(item => String(item.id) !== String(deletingPhoto.id)),
      )
      await fetchPhotos({ page: 1, append: false })
      refreshRecentPhotos?.()
    } catch (err) {
      toastApiError(err, "Failed to delete photography")
    } finally {
      setIsDeleting(false)
    }
  }

  const handleRequestSaved = savedRequest => {
    if (savedRequest?.id) setContributionRequest(savedRequest)
    loadContributionRequest()
  }

  const showInitialSkeleton = isCityLoading || isLoading

  return (
    <>
      <CityHubTabHeader
        title={tab.pageTitle}
        subtitle={tab.pageSubtitle}
        search={search}
        onSearchChange={e => setSearch(e.target.value)}
        searchPlaceholder={tab.searchPlaceholder}
        filterConfig={filterConfig}
        showFilter={tab.showFilter && !isPublic}
        ctaLabel={canContribute ? ctaLabel : null}
        ctaHideIcon={ctaLabel === PHOTOGRAPHY_CTA.edit}
        ctaLoading={ctaBusy}
        ctaDisabled={!canContribute || ctaBusy}
        onCtaClick={handleCtaClick}
      />

      <div
        className="city-hub__photos-grid"
        aria-busy={showInitialSkeleton || isLoadingMore || undefined}
      >
        {showInitialSkeleton
          ? Array.from({ length: SKELETON_COUNT }).map((_, index) => (
              <PhotoCardSkeleton key={`city-photo-skeleton-${index}`} />
            ))
          : null}

        {!showInitialSkeleton &&
          photos.map(photo => (
            <PhotoCard
              key={photo.id}
              photo={photo}
              canManage={
                !isPublic && canManagePhotography(photo, currentUser)
              }
              isLiking={likingId === photo.id}
              onOpen={item => {
                if (isPublic) {
                  openInvite()
                  return
                }
                setPreviewPhotoId(item.id)
              }}
              onEdit={isPublic ? undefined : handleEditPhoto}
              onDelete={isPublic ? undefined : setDeletingPhoto}
              onToggleLike={isPublic ? () => openInvite() : handleToggleLike}
            />
          ))}

        {!showInitialSkeleton && isLoadingMore
          ? Array.from({ length: LOAD_MORE_SKELETON_COUNT }).map((_, index) => (
              <PhotoCardSkeleton key={`city-photo-more-skeleton-${index}`} />
            ))
          : null}

        {!showInitialSkeleton && !isLoadingMore && photos.length === 0 ? (
          <EmptyState
            title={error ? "Unable to load photography" : "No photos found"}
            description={
              error ||
              (!cityId
                ? "Select a city in the banner to browse photography."
                : isPublic
                  ? "Nothing matches this search or filter yet."
                  : !isCityMember
                    ? "Only members of this city can contribute photography. You can still browse as a visitor."
                    : "Nothing matches this search or filter yet.")
            }
            actionText={
              canContribute && !ctaBusy ? ctaLabel : undefined
            }
            onActionClick={
              canContribute && !ctaBusy ? handleCtaClick : undefined
            }
          />
        ) : null}
      </div>
      <CityHubInfiniteSentinel
        sentinelRef={sentinelRef}
        hasMore={hasMore && !isLoading}
      />

      {isPublic ? (
        <PhotographyPreviewModal
          isOpen={Boolean(previewPhotoId)}
          photographyId={previewPhotoId}
          onClose={() => setPreviewPhotoId(null)}
          fetchDetail={fetchPublicPhotoPreview}
        />
      ) : (
        <>
          <BecomeContributorModal
            isOpen={isBecomeOpen && canContribute}
            onClose={() => setIsBecomeOpen(false)}
            cityId={cityId}
            request={contributionRequest}
            onSuccess={handleRequestSaved}
          />

          <AddPhotoModal
            isOpen={isPhotoOpen && canContribute}
            onClose={() => {
              setIsPhotoOpen(false)
              resetPhotoForm()
            }}
            cityId={cityId}
            photographyId={editingPhotoId}
            onSuccess={() => {
              fetchPhotos({ page: 1, append: false })
              refreshRecentPhotos?.()
            }}
          />

          <PhotographyPreviewModal
            isOpen={Boolean(previewPhotoId)}
            photographyId={previewPhotoId}
            onClose={() => setPreviewPhotoId(null)}
          />

          <DeleteConfirmation
            isOpen={Boolean(deletingPhoto)}
            onCancel={() => !isDeleting && setDeletingPhoto(null)}
            onConfirm={handleDeleteConfirm}
            title="Delete photography"
            message="Are you sure you want to delete this photography? This action cannot be undone."
            cancelText="Cancel"
            confirmText="Delete"
            isDeleting={isDeleting}
          />
        </>
      )}
    </>
  )
}

export default CityHubPhotographyTab
